import React, { useState } from "react";
import EditorialArticleView from "./EditorialArticleView";
import ShareButton from "../../ShareButton";

/**
 * EditorialCard — renders in the Workbrook tab feed.
 *
 * Per PRD §5.3:
 *   - Shows headline, category tag, preview text, timestamp
 *   - "Read More" CTA opens the full article inside the feed (in-app reading)
 *   - Canonical URL points back to the blog for SEO
 *
 * Props:
 *   card — feed card object with:
 *     title, preview_text, category, canonical_url, image_url, created_at
 */
const EditorialCard = ({ card }) => {
  const [showFullArticle, setShowFullArticle] = useState(false);

  const categoryColors = {
    "Career Advice":      { bg: "#eff6ff", color: "#1d4ed8" },
    "Hiring Insights":    { bg: "#f0fdf4", color: "#15803d" },
    "Industry Insights":  { bg: "#faf5ff", color: "#7c3aed" },
    "Product Update":     { bg: "#fff7ed", color: "#c2410c" },
  };

  const meta = card.metadata || {};
  const category = card.category || meta.category || "Career Advice";
  const catStyle = categoryColors[category] || categoryColors["Career Advice"];

  if (showFullArticle) {
    return (
      <EditorialArticleView
        card={card}
        onBack={() => setShowFullArticle(false)}
      />
    );
  }

  return (
    <div style={styles.card}>
      {/* Category tag */}
      <div style={styles.categoryRow}>
        <span style={{ ...styles.categoryTag, backgroundColor: catStyle.bg, color: catStyle.color }}>
          {category}
        </span>
        {card.broadcast_flag === true && (
          <span style={styles.broadcastBadge}>BROADCAST</span>
        )}
      </div>

      {/* Image (if present) */}
      {card.image_url && (
        <div style={styles.imageWrap}>
          <img src={card.image_url} alt={card.title} style={styles.image} />
        </div>
      )}

      {/* Title */}
      <h3 style={styles.title}>{card.title}</h3>

      {/* Preview text */}
      <p style={styles.preview}>{card.preview_text}</p>

      {/* Footer: timestamp + Read More + Share */}
      <div style={styles.footer}>
        <span style={styles.timestamp}>{formatTime(card.created_at)}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <ShareButton
            url={card.canonical_url || `${window.location.origin}/feed?card=${card.id}`}
            title={card.title}
            text={card.title}
          />
          <button style={styles.readMoreBtn} onClick={() => setShowFullArticle(true)}>
            Read More →
          </button>
        </div>
      </div>
    </div>
  );
};

function formatTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - date) / (1000 * 60 * 60);
  if (diffHrs < 1) return "Just now";
  if (diffHrs < 24) return `${Math.floor(diffHrs)}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "1d ago";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const styles = {
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    padding: "20px",
  },
  categoryRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  categoryTag: {
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 10px",
    borderRadius: "4px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  broadcastBadge: {
    fontSize: "10px",
    fontWeight: "700",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    letterSpacing: "0.3px",
  },
  imageWrap: {
    marginBottom: "12px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    maxHeight: "240px",
    objectFit: "cover",
    display: "block",
  },
  title: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
    margin: "0 0 8px 0",
    lineHeight: 1.3,
  },
  preview: {
    fontSize: "14px",
    color: "#475569",
    margin: "0 0 14px 0",
    lineHeight: 1.5,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "10px",
    borderTop: "1px solid #f1f5f9",
  },
  timestamp: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  readMoreBtn: {
    background: "none",
    border: "none",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3a6ed4",
    cursor: "pointer",
    padding: "0",
  },
};

export default EditorialCard;
